/**
 * Methodology matrix — named policy cells from external lessons archive.
 * Soft≪AA-safe assist only; Claim B chase never included.
 */
import type { JudgmentAblateMode, JudgmentAssistKnobs } from "./knobs";
import {
  JUDGMENT_ASSIST_DEFAULTS,
  setJudgmentAblate,
  setJudgmentAssistKnobs,
  resetJudgmentAblate,
  resetJudgmentAssistKnobs,
} from "./knobs";

/** Assist strength tiers (RTS autocast lesson). */
export type AssistTier = "off" | "whisper" | "soft" | "medium";

export type MatrixModuleFlags = {
  /** F.E.A.R.: mute rescue when healer feet in packChew AA. */
  fearSelfInAaBlock: boolean;
  /** Legible: min seconds between rescue activations per healer. */
  rescueCooldownSec: number;
  /** Rescue geometry: healer staff sit vs tank-side screen. */
  rescueGeometry: "healer_sit" | "tank_screen";
  /** DAO-style: require danger/crit gate (already via crit01); stricter = higher crit. */
  daoStrictCrit: boolean;
};

export type MatrixCell = {
  id: string;
  /** Short methodology tags. */
  tags: string[];
  ablate: JudgmentAblateMode;
  tier: AssistTier;
  modules: MatrixModuleFlags;
  knobs: Partial<JudgmentAssistKnobs>;
};

const TIER_KNOBS: Record<AssistTier, Partial<JudgmentAssistKnobs>> = {
  off: {
    rescuePullMax: 0,
    rescueKitFollowMul: 0,
    jointPullMax: 0,
    seatFilterScore: 0,
  },
  whisper: {
    rescueBondMin: 80,
    rescuePullMax: 0.05,
    rescueMinCrit01: 0.7,
    rescueKitFollowMul: 0,
    jointInsightMin: 85,
    jointPullMax: 0.04,
    jointMinDistPx: 180,
    seatFilterScore: 0,
  },
  soft: {
    ...JUDGMENT_ASSIST_DEFAULTS,
    rescuePullMax: 0.08,
    rescueMinCrit01: 0.6,
    jointPullMax: 0.05,
    jointMinDistPx: 160,
    seatFilterScore: 0,
  },
  /** Known Soft-regressing band — reject marker. */
  medium: {
    rescueBondMin: 55,
    rescuePullMax: 0.28,
    rescueMinCrit01: 0.4,
    rescueKitFollowMul: 0.32,
    jointInsightMin: 55,
    jointPullMax: 0.18,
    jointMinDistPx: 140,
    seatFilterScore: 0,
  },
};

const MOD_OFF: MatrixModuleFlags = {
  fearSelfInAaBlock: false,
  rescueCooldownSec: 0,
  rescueGeometry: "healer_sit",
  daoStrictCrit: false,
};

/**
 * Systematic screen grid — pairwise then promising triples.
 * Aggressive medium cells included once for reject confirmation.
 */
export const MATRIX_SCREEN_CELLS: readonly MatrixCell[] = [
  {
    id: "baseline_off",
    tags: ["rts-off", "blackboard"],
    ablate: "off",
    tier: "off",
    modules: { ...MOD_OFF },
    knobs: TIER_KNOBS.off,
  },
  {
    id: "ship_soft_rescue",
    tags: ["rts-soft", "rescue", "dao-gates"],
    ablate: "rescue",
    tier: "soft",
    modules: { ...MOD_OFF },
    knobs: { ...TIER_KNOBS.soft, jointPullMax: 0 },
  },
  {
    id: "whisper_rescue",
    tags: ["rts-whisper", "rescue"],
    ablate: "rescue",
    tier: "whisper",
    modules: { ...MOD_OFF },
    knobs: { ...TIER_KNOBS.whisper, jointPullMax: 0 },
  },
  {
    id: "whisper_rescue_fear",
    tags: ["rts-whisper", "rescue", "fear"],
    ablate: "rescue",
    tier: "whisper",
    modules: { ...MOD_OFF, fearSelfInAaBlock: true },
    knobs: { ...TIER_KNOBS.whisper, jointPullMax: 0 },
  },
  {
    id: "soft_rescue_fear",
    tags: ["rts-soft", "rescue", "fear"],
    ablate: "rescue",
    tier: "soft",
    modules: { ...MOD_OFF, fearSelfInAaBlock: true },
    knobs: { ...TIER_KNOBS.soft, jointPullMax: 0 },
  },
  {
    id: "soft_rescue_cooldown",
    tags: ["rts-soft", "rescue", "legible-clamp"],
    ablate: "rescue",
    tier: "soft",
    modules: { ...MOD_OFF, rescueCooldownSec: 1.2 },
    knobs: { ...TIER_KNOBS.soft, jointPullMax: 0 },
  },
  {
    id: "whisper_rescue_dao_strict",
    tags: ["rts-whisper", "rescue", "dao-strict"],
    ablate: "rescue",
    tier: "whisper",
    modules: { ...MOD_OFF, daoStrictCrit: true },
    knobs: {
      ...TIER_KNOBS.whisper,
      jointPullMax: 0,
      rescueMinCrit01: 0.8,
    },
  },
  {
    id: "whisper_tank_screen",
    tags: ["rts-whisper", "rescue", "tank-screen"],
    ablate: "rescue",
    tier: "whisper",
    modules: { ...MOD_OFF, rescueGeometry: "tank_screen" },
    knobs: { ...TIER_KNOBS.whisper, jointPullMax: 0 },
  },
  {
    id: "whisper_joint_far",
    tags: ["rts-whisper", "joint", "boids"],
    ablate: "joint",
    tier: "whisper",
    modules: { ...MOD_OFF },
    knobs: { ...TIER_KNOBS.whisper, rescuePullMax: 0 },
  },
  {
    id: "soft_joint_far",
    tags: ["rts-soft", "joint", "boids"],
    ablate: "joint",
    tier: "soft",
    modules: { ...MOD_OFF },
    knobs: { ...TIER_KNOBS.soft, rescuePullMax: 0 },
  },
  {
    id: "whisper_both_far",
    tags: ["rts-whisper", "rescue", "joint", "boids"],
    ablate: "both",
    tier: "whisper",
    modules: { ...MOD_OFF },
    knobs: TIER_KNOBS.whisper,
  },
  {
    id: "whisper_both_fear",
    tags: ["rts-whisper", "rescue", "joint", "fear", "boids"],
    ablate: "both",
    tier: "whisper",
    modules: { ...MOD_OFF, fearSelfInAaBlock: true },
    knobs: TIER_KNOBS.whisper,
  },
  {
    id: "soft_both_fear_cooldown",
    tags: ["rts-soft", "rescue", "joint", "fear", "legible-clamp"],
    ablate: "both",
    tier: "soft",
    modules: {
      ...MOD_OFF,
      fearSelfInAaBlock: true,
      rescueCooldownSec: 1.5,
    },
    knobs: TIER_KNOBS.soft,
  },
  {
    id: "soft_rescue_fear_cooldown",
    tags: ["rts-soft", "rescue", "fear", "legible-clamp"],
    ablate: "rescue",
    tier: "soft",
    modules: {
      ...MOD_OFF,
      fearSelfInAaBlock: true,
      rescueCooldownSec: 1.2,
    },
    knobs: { ...TIER_KNOBS.soft, jointPullMax: 0 },
  },
  {
    id: "whisper_rescue_fear_cooldown",
    tags: ["rts-whisper", "rescue", "fear", "legible-clamp"],
    ablate: "rescue",
    tier: "whisper",
    modules: {
      ...MOD_OFF,
      fearSelfInAaBlock: true,
      rescueCooldownSec: 1.0,
    },
    knobs: { ...TIER_KNOBS.whisper, jointPullMax: 0 },
  },
  {
    id: "medium_both_REJECT",
    tags: ["rts-medium", "reject-marker"],
    ablate: "both",
    tier: "medium",
    modules: { ...MOD_OFF },
    knobs: TIER_KNOBS.medium,
  },
  // ── Wall-5 dual-seat staff *filter* (no pull / Claim B) ──
  {
    id: "seat_filter_whisper",
    tags: ["seat-filter", "dual-seat", "insight"],
    ablate: "seat",
    tier: "whisper",
    modules: { ...MOD_OFF },
    knobs: {
      ...TIER_KNOBS.off,
      jointInsightMin: 70,
      seatFilterScore: 12,
    },
  },
  {
    id: "seat_filter_soft",
    tags: ["seat-filter", "dual-seat", "insight"],
    ablate: "seat",
    tier: "soft",
    modules: { ...MOD_OFF },
    knobs: {
      ...TIER_KNOBS.off,
      jointInsightMin: 60,
      seatFilterScore: 18,
    },
  },
  {
    id: "seat_filter_strong",
    tags: ["seat-filter", "dual-seat", "insight"],
    ablate: "seat",
    tier: "soft",
    modules: { ...MOD_OFF },
    knobs: {
      ...TIER_KNOBS.off,
      jointInsightMin: 50,
      seatFilterScore: 28,
    },
  },
  {
    id: "whisper_rescue_seat_filter",
    tags: ["rts-whisper", "rescue", "seat-filter", "fear"],
    ablate: "rescue",
    tier: "whisper",
    modules: { ...MOD_OFF, fearSelfInAaBlock: true },
    knobs: {
      ...TIER_KNOBS.whisper,
      jointPullMax: 0,
      jointInsightMin: 70,
      seatFilterScore: 12,
    },
  },
  {
    id: "soft_rescue_seat_filter",
    tags: ["rts-soft", "rescue", "seat-filter", "fear", "legible-clamp"],
    ablate: "rescue",
    tier: "soft",
    modules: {
      ...MOD_OFF,
      fearSelfInAaBlock: true,
      rescueCooldownSec: 1.2,
    },
    knobs: {
      ...TIER_KNOBS.soft,
      jointPullMax: 0,
      jointInsightMin: 60,
      seatFilterScore: 18,
    },
  },
  {
    id: "seat_filter_whisper_joint_far",
    tags: ["seat-filter", "joint", "boids", "rts-whisper"],
    ablate: "both",
    tier: "whisper",
    modules: { ...MOD_OFF, fearSelfInAaBlock: true },
    knobs: {
      ...TIER_KNOBS.whisper,
      seatFilterScore: 12,
    },
  },
];

/** Runtime module flags applied with each cell. */
let activeModules: MatrixModuleFlags = { ...MOD_OFF };

export function getMatrixModules(): MatrixModuleFlags {
  return activeModules;
}

export function applyMatrixCell(cell: MatrixCell): void {
  resetJudgmentAssistKnobs();
  setJudgmentAblate(cell.ablate);
  setJudgmentAssistKnobs(cell.knobs);
  activeModules = { ...cell.modules };
}

export function resetMatrixCell(): void {
  resetJudgmentAssistKnobs();
  resetJudgmentAblate();
  activeModules = { ...MOD_OFF };
}
